import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { authHeader, formsFetch, unwrap } from "../lib/formsApi";
import type { Prefill } from "../questionnaire/prefill";

import { deleteSupportingDocument, suggestAnswers, uploadSupportingDocument } from "./usePreviewFileActions";

export const useSupportingDocuments = (
    assessmentId: string,
    token: string | undefined,
    enabled: boolean
): Prefill | undefined => {
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => ["supporting-documents", assessmentId], [assessmentId]);
    const { data } = useQuery({
        queryKey,
        queryFn: async ({ signal }) =>
            unwrap(
                await formsFetch.GET("/api/supporting-documents", {
                    params: { query: { assessmentId } },
                    headers: authHeader(token),
                    signal,
                }),
                "list supporting documents"
            ).documents,
        enabled: enabled && !!assessmentId,
        retry: false,
        refetchInterval: 240_000,
    });

    return useMemo(() => {
        if (!data) return undefined;
        const refresh = () => queryClient.invalidateQueries({ queryKey });
        return {
            documents: data.map((doc) => ({
                key: doc.path,
                name: doc.name,
                size: doc.size,
                open: () => window.open(doc.url, "_blank", "noopener,noreferrer"),
                remove: async () => {
                    try {
                        await deleteSupportingDocument(token, doc.path, assessmentId);
                    } finally {
                        await refresh();
                    }
                },
            })),
            add: async (files: File[]) => {
                try {
                    const paths = await Promise.all(
                        files.map((file) => uploadSupportingDocument(token, file, assessmentId))
                    );
                    return await suggestAnswers(
                        token,
                        paths.flatMap((p) => (p ? [p] : [])),
                        assessmentId
                    );
                } finally {
                    await refresh();
                }
            },
        };
    }, [data, queryClient, queryKey, token, assessmentId]);
};
